/**
 * Notifications Page
 * View and manage notifications
 */

import React from 'react';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import {
  Bell,
  CheckCheck,
  MessageCircle,
  Ship,
  Star,
  MapPin,
  User,
  Trash2,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

function NotificationsPage() {
  const { notifications, markAsRead, markAllAsRead, deleteNotification, clearNotifications, unreadCount, loading, fetchNotifications } = useNotifications();
  const { isHelper } = useAuth();

  const getNotificationIcon = (type) => {
    const icons = {
      message: MessageCircle,
      new_message: MessageCircle,
      request: Ship,
      new_request: Ship,
      rating: Star,
      rating_received: Star,
      application: User,
      selected: CheckCheck,
      service: MapPin,
      service_completed: CheckCheck,
      completion_reminder: Bell,
      request_modified: Bell,
      request_cancelled: Bell,
    };
    return icons[type] || Bell;
  };

  const getNotificationLink = (notification) => {
    const requestId = notification.request_id || notification.reference_id;
    // Use correct route prefix based on user type
    const requestsRoute = isHelper ? '/helper/requests' : '/owner/requests';

    switch (notification.type) {
      case 'message':
      case 'new_message': {
        // Always route to request page when possible (chat is embedded in request details)
        const notifData = typeof notification.data === 'string' ? (() => { try { return JSON.parse(notification.data); } catch(e) { return null; } })() : notification.data;
        const msgRequestId = notifData?.request_id
          || (notification.reference_type === 'request' ? notification.reference_id : null);
        if (msgRequestId) {
          return isHelper ? `/helper/requests/${msgRequestId}` : `/owner/requests/${msgRequestId}`;
        }
        // Fallback: route to request list (not standalone chat page)
        return isHelper ? '/helper/requests' : '/owner/requests';
      }
      case 'request':
      case 'new_request':
        // New requests go to available requests for helpers, owner requests for owners
        return isHelper ? '/helper/requests' : (requestId ? `${requestsRoute}/${requestId}` : null);
      case 'application':
        // Applications are for owners to review
        return requestId ? `/owner/requests/${requestId}` : null;
      case 'request_modified':
      case 'request_cancelled':
      case 'helper_withdrawn':
        return requestId ? `${requestsRoute}/${requestId}` : null;
      case 'selected':
      case 'service':
      case 'service_completed':
      case 'completion_reminder':
        return requestId ? `${requestsRoute}/${requestId}` : null;
      case 'rating':
      case 'rating_received':
        return requestId ? `${requestsRoute}/${requestId}` : null;
      default:
        return null;
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-navy-900">
            Notifications
          </h1>
          <p className="text-navy-600 mt-1">
            {unreadCount > 0
              ? `You have ${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
              : 'All caught up!'}
          </p>
        </div>
        <div className="flex space-x-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="btn-secondary py-2"
            >
              <CheckCheck className="h-4 w-4 mr-1" />
              Mark all read
            </button>
          )}
          {notifications.length > 0 && (
            <button
              onClick={clearNotifications}
              className="p-2 text-navy-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      {loading ? (
        <div className="card text-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-navy-600">Loading notifications...</p>
        </div>
      ) : notifications.length === 0 ? (
        <div className="card text-center py-12">
          <Bell className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-navy-900 mb-2">
            No notifications
          </h3>
          <p className="text-navy-600">
            You'll see updates about your requests and services here
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-luxury divide-y divide-gray-100">
          {notifications.map((notification) => {
            const Icon = getNotificationIcon(notification.type);
            const link = getNotificationLink(notification);
            const Wrapper = link ? Link : 'div';

            return (
              <Wrapper
                key={notification.id}
                to={link}
                onClick={() => handleNotificationClick(notification)}
                className={`flex items-start p-4 transition-colors ${
                  !notification.read ? 'bg-primary-50' : 'hover:bg-gray-50'
                } ${link ? 'cursor-pointer' : ''}`}
              >
                {/* Icon */}
                <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  !notification.read ? 'bg-primary-100' : 'bg-gray-100'
                }`}>
                  <Icon className={`h-5 w-5 ${
                    !notification.read ? 'text-primary-600' : 'text-navy-500'
                  }`} />
                </div>

                {/* Content */}
                <div className="ml-4 flex-1">
                  <p className={`${!notification.read ? 'font-medium text-navy-900' : 'text-navy-700'}`}>
                    {notification.title || notification.message}
                  </p>
                  {notification.body && (
                    <p className="text-sm text-navy-500 mt-1">
                      {notification.body}
                    </p>
                  )}
                  <p className="text-xs text-navy-400 mt-2">
                    {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
                  </p>
                </div>

                {/* Delete button */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    deleteNotification(notification.id);
                  }}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors flex-shrink-0 ml-2"
                  title="Delete notification"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>

                {/* Unread indicator */}
                {!notification.read && (
                  <div className="h-2 w-2 bg-primary-600 rounded-full flex-shrink-0 mt-2" />
                )}
              </Wrapper>
            );
          })}
        </div>
      )}

      {/* Sample notifications for demo purposes */}
      {notifications.length === 0 && (
        <div className="mt-8">
          <h3 className="text-sm font-medium text-navy-500 mb-3">
            Example notifications you might receive:
          </h3>
          <div className="bg-gray-50 rounded-xl p-4 space-y-3 text-sm text-navy-600">
            <div className="flex items-center">
              <Ship className="h-4 w-4 mr-2 text-primary-600" />
              "A helper has applied to your request at Monaco"
            </div>
            <div className="flex items-center">
              <MessageCircle className="h-4 w-4 mr-2 text-primary-600" />
              "New message from Captain James"
            </div>
            <div className="flex items-center">
              <Star className="h-4 w-4 mr-2 text-gold-500" />
              "You received a 5-star rating!"
            </div>
            <div className="flex items-center">
              <MapPin className="h-4 w-4 mr-2 text-primary-600" />
              "New mooring request in Nice, France"
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationsPage;
